import zionDebug from '@fs/zion-debug'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useFeatureFlags } from '@fs/zion-flags'
import { useIsOptedIn } from '@fs/zion-soft-launch'
import { useLocation, useQueryParams } from '@fs/zion-router'
import {
  useSls,
  useSlsDispatchContext,
  useSlsStateContext,
  getReferencedImageArksToDisplayPersons,
  getImage,
} from '@fs/zion-sls'
import { useInfoSheet, useOverlay, usePageWidth } from '@fs/zion-ui'
import AttachMatchTreeProvider from '@fs/zion-attach-match-tree-provider'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorBoundaryFallback from '../../../shared/components/errorBoundaryFallback/ErrorBoundaryFallback'
import ErrorOverlay from '../../../shared/components/errorOverlay/ErrorOverlay'
import R2Image from '../r2imageBrowser'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../providers/r2Provider/R2Context'
import FeedbackOverlay from '../../../shared/components/feedbackOverlay/FeedbackOverlay'
import { headers } from '../../../shared/services/config'
import { errorCollector } from '../../../shared/utils/utils'
import SharedUsabilla from '../../../shared/components/sharedUsabilla/SharedUsabilla'
import FullTextContextProvider from '../../views/fullText/providers/FullTextContext'
import { IndexingErrorOverlay } from '../../views/indexing/components/overlays'
import useRecords from '../../views/indexing/hooks/useRecords'
import OverlayContextProvider from '../../../shared/providers/overlayContextProvider/OverlayContext'
import additionalFieldTypes from '../../views/indexing/services/userTestCache/sls-types/field-supplement.json'
import ViewAnotherItemDialog from './components/viewAnotherItemDialog/ViewAnotherItemDialog'
import { getImageTitleFromGroup, getAttachmentsUrls, getArkIdArray } from './utils/utils'
import SideSheet from '../sideSheet/SideSheet'
import { useLocalizeNames } from './hooks/useLocalizeNames'
import NavigationTopBar from './components/navigationTopBar/NavigationTopBar'
import SelectPersonToAttachToFamilyTreeInfoSheet from '../generic/panels/SelectPersonToAttachToFamilyTreeInfoSheet'
/** Not logged in game plan
  1. Check if the image requires login
  2. If it does, redirect to login
  5. If not then create an anonymous session that RMS requires
  4. Don't load the image data for now.  We may do that later.
  5. If the patron is already logged in and the image doesn't require login, continue as normal
*/
const debug = zionDebug('rip:NamesView')

const NamesView = ({ validSession }) => {
  const r2State = useR2StateContext()
  const {
    rip_sharedImageViewer_feedback_RII: feedbackFlag,
    rip_sharedImageViewer_fullTextAttachFlowOptIn: fullTextAttachFlowFlagOptIn,
    rip_sharedImageViewer_fullTextAttachFlow_v1: fullTextAttachFlowV1,
    rip_sharedImageViewer_fullTextAttachFlow_v2: fullTextAttachFlowV2,
  } = useFeatureFlags([
    'rip_sharedImageViewer_fullTextAttachFlowOptIn',
    'rip_sharedImageViewer_fullTextAttachFlow_v1',
    'rip_sharedImageViewer_fullTextAttachFlow_v2',
    'rip_sharedImageViewer_feedback_RII',
  ])
  const { isOptedIn, loading: loadingAttachFlowPrefs } = useIsOptedIn({
    featureFlag: 'rip_sharedImageViewer_fullTextAttachFlow_v1',
  })
  let fullTextAttachFlowV1IsOn = fullTextAttachFlowV1.isOn
  if (fullTextAttachFlowFlagOptIn.isOn) {
    fullTextAttachFlowV1IsOn =
      r2State.attachFlowOptInLocalChange === null
        ? !loadingAttachFlowPrefs && isOptedIn
        : r2State.attachFlowOptInLocalChange
  }

  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const [t] = useTranslation()
  const location = useLocation()
  const infoSheetOverlay = useInfoSheet()
  const viewIndexOverlay = useOverlay()
  const editIndexOverlay = useOverlay()
  const addPersonGenericOverlay = useOverlay()
  const addPersonPage2GenericOverlay = useOverlay()
  const dragNamesOverlay = useOverlay()
  const removePersonOverlay = useOverlay()
  const attachToTreeFirstPanel = useOverlay()
  const viewAnotherItemOverlay = useOverlay()
  const finishedCorrectingMetadataOverlay = useOverlay()
  const editSharedHighlightPage1Overlay = useOverlay()
  const editSharedHighlightPage2Overlay = useOverlay()
  const editHighlightsOverlay = useOverlay()
  const editMetadataOverlay = useOverlay()
  const editSharedFieldPage1Overlay = useOverlay()
  const editSharedFieldPage2Overlay = useOverlay()
  const editDanglingFieldOverlay = useOverlay()
  const editOtherFieldsOverlay = useOverlay()
  const editRelationshipsOverlay = useOverlay()
  const editAgeOverlay = useOverlay()
  const editEventsOverlay = useOverlay()
  const editEssentialInfoOverlay = useOverlay()
  const eventPanelOverlay = useOverlay()
  const sharedEventOverlay = useOverlay()
  const linkExistingEventOverlay = useOverlay()
  const highlightsPanelOverlay = useOverlay()
  const highlightsPanelOldOverlay = useOverlay()
  const addNamesOverlay = useOverlay()
  const softLaunchAttachFlowV1Overlay = useOverlay()
  const selectPersonToAttachToTreeInfoSheetOverlay = useOverlay()
  const groupHistoryOverlay = useOverlay()
  const editHouseholdOverlay = useOverlay()
  const { query } = useQueryParams()

  const zionSlsDispatch = useSlsDispatchContext()
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const imageArk = location.pathname.split('/').pop()
  const [errorLoading, setErrorLoading] = useState(false)
  const r2ShouldLoadIndexedData = r2State.shouldLoadIndexedData
  const { images: slsImages, arksToLoad, currentImageArk } = zionSlsState

  const currentImage = useMemo(() => getImage(slsImages, currentImageArk) || {}, [currentImageArk, slsImages])
  const additionalImageArks = useCallback(
    () => getReferencedImageArksToDisplayPersons(currentImage, []),
    [currentImage]
  )

  const handleError = useCallback((err, info) => {
    debug('well, sorry about the bad news', err, info)
    errorCollector(err, null, {
      codeId: 1001,
      status: err?.response?.status,
      responseBody: err?.response?.data,
      ...info,
    })
  }, [])

  const areImagesLoaded = useCallback(
    (imageArks) => {
      if (!imageArks) return false
      const loadedArks = new Set(slsImages?.map((img) => img.partitionKey))
      return imageArks.every((ark) => loadedArks.has(ark))
    },
    [slsImages]
  )

  // Preparing the objects for the AttachMatchTreeProvider to get the attachmentIds and matchIds in context.
  const personIdsOrderForImage = zionSlsState.personOrder?.find((po) => po.partitionKey === currentImageArk)

  const { isJiapu, allRecords } = useRecords({
    images: slsImages,
    currentImageArk,
    thumbnails,
    personIdsOrder: personIdsOrderForImage?.personIds,
  })
  const attachmentUrls = useMemo(() => getAttachmentsUrls(currentImage), [currentImage])
  const arkIdArray = useMemo(() => getArkIdArray(allRecords, currentImageArk), [currentImageArk, allRecords])

  const slsImageArksToLoad = useMemo(() => {
    // skip load more images if is fullText
    if (query.view === 'fullText') {
      return [imageArk]
    }
    // ** Once the first image (current image) loads, load the other images referenced in this image's data\
    if (!isJiapu && r2ShouldLoadIndexedData) {
      const imgs = arksToLoad?.length ? arksToLoad : []
      // Always include current image, arksToLoad, and additional referenced images; get rid of duplicates
      return [...new Set([imageArk, ...imgs, ...additionalImageArks()])]
    }
    return []
  }, [imageArk, isJiapu, r2ShouldLoadIndexedData, additionalImageArks, arksToLoad, query])

  const { loading: loadingSls, error } = useSls({
    imageArks: slsImageArksToLoad,
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    sort: true,
    dispatch: true,
    additionalFieldTypes,
    enableHook: r2ShouldLoadIndexedData, // Don't load image data if this is a not signed in image
  })

  useEffect(() => {
    // If the loadingSls took more than 10 seg we show a error message
    let timeoutId

    if (loadingSls) {
      timeoutId = setTimeout(() => {
        if (loadingSls) {
          setErrorLoading(true)
        }
      }, 10000) // 10 seconds
    }

    return () => clearTimeout(timeoutId) // Clear the timeout
  }, [loadingSls])

  // Calculate loading state based on actual data availability
  const loading = useMemo(() => {
    if (!r2ShouldLoadIndexedData) return false
    if (loadingSls) return true
    return !areImagesLoaded(slsImageArksToLoad || [])
  }, [r2ShouldLoadIndexedData, loadingSls, slsImageArksToLoad, areImagesLoaded])

  // Use custom hook to handle localize names logic
  useLocalizeNames({
    slsImageArksToLoad,
    currentImage,
    loadingSls,
    imageArk,
  })
  // We need this useEffect because we are updating state in an external hook
  useEffect(() => {
    if (zionSlsState.loading !== loading) {
      zionSlsDispatch({ type: 'SET_DATA', value: { t, loading } })
    }
  }, [zionSlsDispatch, loading, t, zionSlsState.loading])

  debug('Render: ', {
    slsImageArksToLoad,
    imageDatumsLoaded: slsImages,
    shouldLoadIndexedData: r2ShouldLoadIndexedData,
    imagePermission: r2State.imagePermission,
    currentImageArk,
    imageArk,
    error,
    query,
    loading,
    loadingSls,
    thumbnails,
    newImage: getImage(slsImages, imageArk),
    currentImage,
    r2State,
    zionSlsState,
  })

  const imageTitle = useMemo(() => getImageTitleFromGroup(r2State.groupData), [r2State.groupData])

  /** Since we control the login, we shouldn't load components if the session is not valid.
   * It causes the app to reload over and over. BAD!!
   * Making api calls that return 401s will cause the infinite reload.  Just don't load the components until
   * the session is checked and valid in ViewManager ************* */
  return (
    <AttachMatchTreeProvider attachmentUrls={attachmentUrls} arkIdArray={arkIdArray}>
      <OverlayContextProvider
        editIndexOverlay={editIndexOverlay}
        viewIndexOverlay={viewIndexOverlay}
        addPersonGenericOverlay={addPersonGenericOverlay}
        addPersonPage2GenericOverlay={addPersonPage2GenericOverlay}
        dragNamesOverlay={dragNamesOverlay}
        removePersonOverlay={removePersonOverlay}
        infoSheetOverlay={infoSheetOverlay}
        selectPersonToAttachToTreeInfoSheetOverlay={selectPersonToAttachToTreeInfoSheetOverlay}
        viewAnotherItemOverlay={viewAnotherItemOverlay}
        finishedCorrectingMetadataOverlay={finishedCorrectingMetadataOverlay}
        editSharedHighlightPage1Overlay={editSharedHighlightPage1Overlay}
        editSharedHighlightPage2Overlay={editSharedHighlightPage2Overlay}
        editHighlightsOverlay={editHighlightsOverlay}
        editMetadataOverlay={editMetadataOverlay}
        editSharedFieldPage1Overlay={editSharedFieldPage1Overlay}
        editSharedFieldPage2Overlay={editSharedFieldPage2Overlay}
        editDanglingFieldOverlay={editDanglingFieldOverlay}
        editOtherFieldsOverlay={editOtherFieldsOverlay}
        editRelationshipsOverlay={editRelationshipsOverlay}
        editAgeOverlay={editAgeOverlay}
        editEventsOverlay={editEventsOverlay}
        editEssentialInfoOverlay={editEssentialInfoOverlay}
        eventPanelOverlay={eventPanelOverlay}
        sharedEventOverlay={sharedEventOverlay}
        linkExistingEventOverlay={linkExistingEventOverlay}
        highlightsPanelOverlay={highlightsPanelOverlay}
        highlightsPanelOldOverlay={highlightsPanelOldOverlay}
        addNamesOverlay={addNamesOverlay}
        softLaunchAttachFlowV1Overlay={softLaunchAttachFlowV1Overlay}
        attachToTreeOverlay={attachToTreeFirstPanel}
        groupHistoryOverlay={groupHistoryOverlay}
        editHouseholdOverlay={editHouseholdOverlay}
      >
        <ErrorBoundary errorHandler={handleError} FallbackComponent={ErrorBoundaryFallback}>
          <ViewAnotherItemDialog overlay={viewAnotherItemOverlay} />

          <FullTextContextProvider>
            <NavigationTopBar
              imageTitle={imageTitle}
              itemSelectorOverlay={viewAnotherItemOverlay}
              fullTextAttachFlowV1IsOn={fullTextAttachFlowV1IsOn}
            />
            {validSession && <R2Image />}
            {!isSmallScreen && feedbackFlag.isOn && <FeedbackOverlay />}
            <SideSheet loading={loading || loadingSls} imageTitle={imageTitle} />

            {/**
             * Note, this new infosheet is not just for fulltext, it's also for RII
             */}
            {(fullTextAttachFlowV1IsOn || fullTextAttachFlowV2.isOn) && (
              <SelectPersonToAttachToFamilyTreeInfoSheet
                overlay={selectPersonToAttachToTreeInfoSheetOverlay}
                loading={loading || loadingSls}
                imageTitle={imageTitle}
                fullTextAttachFlowV1IsOn={fullTextAttachFlowV1IsOn}
              />
            )}
          </FullTextContextProvider>
          <SharedUsabilla />
          <ErrorOverlay
            error={errorLoading}
            setError={setErrorLoading}
            title={t('ssl-loading-error.heading', 'Unable to load the data')}
            message={t(
              'ssl-loading-error.content',
              'Loading record data is taking longer than expected.  Please try again later.'
            )}
          />
          {error && <IndexingErrorOverlay error={error} />}
        </ErrorBoundary>
      </OverlayContextProvider>
    </AttachMatchTreeProvider>
  )
}

export default NamesView
